import { useEffect, useRef, useState } from "react";
import ChannelConnection from "../../js/connection/ChannelConnection";
import { useNavigate, useParams } from "react-router";
import useChannelValidation from "./hooks/useChannelValidation";
import LoadingIndicator from "../LoadingIndicator";
import SourceInputDropdown from "./SourceInputDropdown";
import liveswitch from "fm.liveswitch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faMicrophone,
    faMicrophoneSlash,
    faVideo,
    faVideoSlash,
    faShieldAlt,
    faCheckCircle,
    faSpinner,
    faCog,
    faLock,
} from "@fortawesome/free-solid-svg-icons";
import holorayLogo from "../../assets/images/png/HoloRay_Logo_256.png";
import { releaseLocalMedia } from "../../js/media/releaseLocalMedia";

export default function ChannelValidationPage({
    connection,
    onConfirm
}: {
    connection: ChannelConnection;
    onConfirm: (lm: liveswitch.LocalMedia) => void;
}) {
    const channelId = useRef(useParams().id);
    const navigate = useNavigate();
    useChannelValidation(connection);
    const hasAccess = true;

    const [localMedia, setLocalMedia] = useState<liveswitch.LocalMedia>();
    const [videoInputs, setVideoInputs] = useState<liveswitch.SourceInput[]>([]);
    const [audioInputs, setAudioInputs] = useState<liveswitch.SourceInput[]>([]);
    const [isMicOn, setMicOn] = useState(true);
    const [isCamOn, setCamOn] = useState(true);
    const [isReady, setReady] = useState(false);
    const [isMirrored, setMirrored] = useState(true);
    const [isTestingAudio, setTestingAudio] = useState(false);
    const [isNetworkReady, setNetworkReady] = useState(false);
    const [highlightDevices, setHighlightDevices] = useState(false);
    const videoPreviewRef = useRef<HTMLDivElement>(null);
    const deviceCardRef = useRef<HTMLDivElement>(null);
    const localMediaRef = useRef<liveswitch.LocalMedia | null>(null);
    const handedOffRef = useRef(false);

    useEffect(() => {
        let cancelled = false;

        async function startLocalMedia() {
            const videoConfig = ChannelConnection.localMediaConfig;
            const lm = new liveswitch.LocalMedia(true, videoConfig, false);
            try {
                const started = await lm.start();
                if (cancelled) {
                    releaseLocalMedia(started);
                    return;
                }
                localMediaRef.current = started;
                setLocalMedia(started);
                setReady(true);
            } catch (e) {
                console.error("Failed to start local media", e);
                if (!cancelled) releaseLocalMedia(lm);
            }
        }

        startLocalMedia();

        return () => {
            cancelled = true;
            // If the user joined, parent owns the LocalMedia — don't stop it here.
            if (!handedOffRef.current) {
                releaseLocalMedia(localMediaRef.current);
                localMediaRef.current = null;
            }
        };
    }, [hasAccess]);

    function goHome() {
        // Explicitly release before navigate so the camera light turns off immediately
        handedOffRef.current = false;
        releaseLocalMedia(localMediaRef.current);
        localMediaRef.current = null;
        setLocalMedia(undefined);
        navigate("/");
    }

    function joinNow() {
        if (!localMedia) return;
        handedOffRef.current = true;
        onConfirm(localMedia);
    }

    // Stagger the "network" system check slightly after media comes online,
    // just so the checklist doesn't flip all four rows at exactly the same instant.
    useEffect(() => {
        if (!isReady) return;
        const timer = setTimeout(() => setNetworkReady(true), 450);
        return () => clearTimeout(timer);
    }, [isReady]);

    // Attach the live video view once localMedia is ready
    useEffect(() => {
        if (!localMedia || !videoPreviewRef.current) return;
        const view = (localMedia as any).getView() as HTMLElement;
        if (!view) return;

        // Ensure the view fills the preview container
        view.style.position = 'absolute';
        view.style.top = '0';
        view.style.left = '0';
        view.style.width = '100%';
        view.style.height = '100%';

        // Style the video element inside
        const videoEl = view.querySelector('video') as HTMLVideoElement | null;
        if (videoEl) {
            videoEl.style.width = '100%';
            videoEl.style.height = '100%';
            videoEl.style.objectFit = 'cover';
            videoEl.style.transform = isMirrored ? 'scaleX(-1)' : 'none';
        }

        videoPreviewRef.current.appendChild(view);

        return () => {
            if (view.parentElement) {
                view.parentElement.removeChild(view);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localMedia]);

    // Keep the mirror preference in sync with the live preview video element
    useEffect(() => {
        const videoEl = videoPreviewRef.current?.querySelector('video') as HTMLVideoElement | null;
        if (videoEl) {
            videoEl.style.transform = isMirrored ? 'scaleX(-1)' : 'none';
        }
    }, [isMirrored, localMedia]);

    useEffect(() => {
        if (!localMedia) return;
        async function getDevices() {
            const videoSourceInputs = await localMedia!.getVideoSourceInputs();
            setVideoInputs(videoSourceInputs);
            const audioSourceInputs = await localMedia!.getAudioSourceInputs();
            setAudioInputs(audioSourceInputs);
        }
        getDevices();
    }, [localMedia]);

    function toggleMic() {
        if (!localMedia) return;
        setMicOn(prev => {
            const next = !prev;
            localMedia.setAudioMuted(!next);
            return next;
        });
    }

    function toggleCam() {
        if (!localMedia) return;
        setCamOn(prev => {
            const next = !prev;
            localMedia.setVideoMuted(!next);
            return next;
        });
    }

    function revealDeviceSettings() {
        deviceCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHighlightDevices(true);
        setTimeout(() => setHighlightDevices(false), 1200);
    }

    function testAudio() {
        if (isTestingAudio) return;
        setTestingAudio(true);
        try {
            const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
            const ctx = new AudioCtx();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.frequency.value = 440;
            gain.gain.value = 0.12;
            osc.connect(gain).connect(ctx.destination);
            osc.start();
            setTimeout(() => {
                osc.stop();
                ctx.close();
            }, 550);
        } catch {
            // Web Audio isn't available — the UI feedback below is enough on its own.
        }
        setTimeout(() => setTestingAudio(false), 1400);
    }

    const userAlias = localStorage.getItem('userAlias') || 'Medical Professional';

    return (
        <div className="cv-root">
            {/* Header */}
            <header className="cv-header">
                <button className="cv-back-link" onClick={goHome}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>Back to Join</span>
                </button>

                <div className="cv-brand">
                    <img src={holorayLogo} alt="HoloRay" className="cv-brand-logo" />
                    <span className="cv-brand-name">HoloXR Light</span>
                </div>

                <div className="cv-hipaa-badge">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    <span>HIPAA Compliant</span>
                </div>
            </header>

            <main className="cv-main">
                <div className="cv-title-wrap">
                    <h1 className="cv-title">Check your camera and microphone</h1>
                    <p className="cv-subtitle">
                        Take a moment to make sure everything looks and sounds right before joining session{" "}
                        <strong>{channelId.current}</strong>.
                    </p>
                </div>

                <div className="cv-grid">
                    {/* Left column — video preview */}
                    <section className="cv-preview-col">
                        <div className="cv-preview-card">
                            <div className="cv-video-frame">
                                <div ref={videoPreviewRef} id="video-preview" className="cv-video-preview">
                                    {!isReady && (
                                        <div className="cv-video-loading">
                                            <LoadingIndicator />
                                            <p>Starting camera…</p>
                                        </div>
                                    )}
                                    {!isCamOn && isReady && (
                                        <div className="cv-cam-off">
                                            <div className="cv-cam-off-avatar">
                                                {userAlias.charAt(0).toUpperCase()}
                                            </div>
                                            <span>{userAlias}</span>
                                        </div>
                                    )}
                                </div>

                                <div className={`cv-cam-status-badge ${isCamOn ? 'on' : 'off'}`}>
                                    <span className="cv-cam-status-dot" />
                                    <span>Camera {isCamOn ? 'on' : 'off'}</span>
                                </div>

                                {/* Floating glass control bar */}
                                <div className="cv-glass-bar">
                                    <button
                                        className={`cv-glass-btn ${!isMicOn ? 'off' : ''}`}
                                        onClick={toggleMic}
                                        title={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
                                    >
                                        <FontAwesomeIcon icon={isMicOn ? faMicrophone : faMicrophoneSlash} />
                                    </button>
                                    <button
                                        className={`cv-glass-btn ${!isCamOn ? 'off' : ''}`}
                                        onClick={toggleCam}
                                        title={isCamOn ? 'Turn off camera' : 'Turn on camera'}
                                    >
                                        <FontAwesomeIcon icon={isCamOn ? faVideo : faVideoSlash} />
                                    </button>
                                    <button
                                        className="cv-glass-btn"
                                        onClick={revealDeviceSettings}
                                        title="Device settings"
                                    >
                                        <FontAwesomeIcon icon={faCog} />
                                    </button>
                                </div>
                            </div>

                            <div className="cv-preview-extra">
                                <label className="cv-mirror-toggle">
                                    <span className="cv-switch">
                                        <input
                                            type="checkbox"
                                            checked={isMirrored}
                                            onChange={e => setMirrored(e.target.checked)}
                                        />
                                        <span className="cv-switch-track" />
                                    </span>
                                    <span>Mirror my video</span>
                                </label>

                                <button
                                    className={`cv-test-audio-btn ${isTestingAudio ? 'testing' : ''}`}
                                    onClick={testAudio}
                                    disabled={isTestingAudio}
                                >
                                    <FontAwesomeIcon icon={faMicrophone} />
                                    <span>{isTestingAudio ? 'Testing…' : 'Test my audio'}</span>
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* Right column — device setup + system check */}
                    <aside className="cv-side-col">
                        <div ref={deviceCardRef} className={`cv-card ${highlightDevices ? 'highlight' : ''}`}>
                            <h3 className="cv-card-title">Device setup</h3>

                            {videoInputs.length > 0 && (
                                <div className="cv-device-row">
                                    <SourceInputDropdown
                                        label="Camera"
                                        inputs={videoInputs}
                                        defaultValue={videoInputs[0].getId()}
                                        onChange={async (input) => {
                                            await localMedia?.changeVideoSourceInput(input);
                                        }}
                                    />
                                </div>
                            )}

                            {audioInputs.length > 0 && (
                                <div className="cv-device-row">
                                    <SourceInputDropdown
                                        label="Microphone"
                                        inputs={audioInputs}
                                        defaultValue={audioInputs[0].getId()}
                                        onChange={async (input) => {
                                            await localMedia?.changeAudioSourceInput(input);
                                        }}
                                    />

                                    <div className={`cv-mic-meter ${isMicOn ? 'active' : 'muted'}`}>
                                        {isMicOn ? (
                                            <>
                                                <span className="cv-mic-bars">
                                                    <span /><span /><span /><span /><span />
                                                </span>
                                                <span className="cv-mic-meter-label">Input detected</span>
                                            </>
                                        ) : (
                                            <span className="cv-mic-meter-label muted">Microphone muted</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="cv-card cv-system-card">
                            <h3 className="cv-card-title">System check</h3>
                            <ul className="cv-check-list">
                                <SystemCheckRow label="Camera" ready={isReady} />
                                <SystemCheckRow label="Microphone" ready={isReady} />
                                <SystemCheckRow label="Permissions" ready={isReady} />
                                <SystemCheckRow label="Network" ready={isReady && isNetworkReady} />
                            </ul>
                        </div>
                    </aside>
                </div>
            </main>

            {/* Footer */}
            <footer className="cv-footer">
                <div className="cv-footer-actions">
                    <button className="cv-btn-secondary" onClick={goHome}>
                        I'll do this later
                    </button>
                    <button
                        className="cv-btn-primary"
                        disabled={!isReady || !localMedia}
                        onClick={joinNow}
                    >
                        <span>Join now</span>
                    </button>
                </div>
                <p className="cv-encryption-note">
                    <FontAwesomeIcon icon={faLock} />
                    Your video and audio are end-to-end encrypted and never recorded without consent.
                </p>
            </footer>

            <style>{`
                .cv-root {
                    height: 100vh;
                    height: 100dvh;
                    max-height: 100dvh;
                    overflow: hidden;
                    background: linear-gradient(160deg, var(--hx-bg, #f4f8ff) 0%, var(--hx-bg-2, #eaf2ff) 55%, #ffffff 100%);
                    display: flex;
                    flex-direction: column;
                    font-family: var(--hx-font, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif);
                    color: var(--hx-ink, #0f172a);
                }

                /* ── Header ───────────────────────────────────────────── */

                .cv-header {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 0.7rem 1.5rem;
                    padding-top: max(0.7rem, env(safe-area-inset-top, 0px));
                    padding-left: max(1.5rem, env(safe-area-inset-left, 0px));
                    padding-right: max(1.5rem, env(safe-area-inset-right, 0px));
                    background: rgba(255, 255, 255, 0.7);
                    backdrop-filter: blur(14px);
                    -webkit-backdrop-filter: blur(14px);
                    border-bottom: 1px solid var(--hx-line, #e2e8f0);
                    flex-shrink: 0;
                }

                .cv-back-link {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: transparent;
                    border: none;
                    color: var(--hx-muted, #64748b);
                    font-size: 0.8rem;
                    font-weight: 600;
                    cursor: pointer;
                    padding: 0.35rem 0.5rem;
                    border-radius: 8px;
                    transition: background 0.15s, color 0.15s;
                    font-family: inherit;
                    flex-shrink: 0;
                }

                .cv-back-link:hover {
                    background: var(--hx-line-2, #f1f5f9);
                    color: var(--hx-ink, #0f172a);
                }

                .cv-brand {
                    display: flex;
                    align-items: center;
                    gap: 0.55rem;
                    margin: 0 auto;
                }

                .cv-brand-logo {
                    height: 24px;
                    width: auto;
                }

                .cv-brand-name {
                    font-size: 0.9rem;
                    font-weight: 700;
                    color: var(--hx-ink, #0f172a);
                    letter-spacing: -0.01em;
                }

                .cv-hipaa-badge {
                    display: flex;
                    align-items: center;
                    gap: 0.35rem;
                    background: rgba(22, 163, 74, 0.08);
                    border: 1px solid rgba(22, 163, 74, 0.25);
                    color: var(--hx-success, #16a34a);
                    font-size: 0.7rem;
                    font-weight: 650;
                    padding: 0.3rem 0.65rem;
                    border-radius: 999px;
                    flex-shrink: 0;
                    white-space: nowrap;
                }

                /* ── Main ─────────────────────────────────────────────── */

                .cv-main {
                    flex: 1;
                    min-height: 0;
                    width: 100%;
                    max-width: 1120px;
                    margin: 0 auto;
                    padding: clamp(0.65rem, 1.5vh, 1.15rem) clamp(1rem, 2vw, 1.5rem);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                .cv-title-wrap {
                    text-align: center;
                    max-width: 640px;
                    margin: 0 auto clamp(0.5rem, 1.2vh, 0.9rem);
                    flex-shrink: 0;
                }

                .cv-title {
                    font-size: clamp(1.15rem, 1.5vw + 0.6rem, 1.55rem);
                    font-weight: 750;
                    color: var(--hx-ink, #0f172a);
                    letter-spacing: -0.02em;
                    margin: 0 0 0.3rem;
                }

                .cv-subtitle {
                    font-size: clamp(0.78rem, 0.3vw + 0.7rem, 0.88rem);
                    color: var(--hx-muted, #64748b);
                    line-height: 1.4;
                    margin: 0;
                }

                .cv-subtitle strong {
                    color: var(--hx-ink-2, #1e293b);
                    font-family: 'SF Mono', 'Fira Code', ui-monospace, monospace;
                    font-weight: 600;
                }

                .cv-grid {
                    flex: 1;
                    min-height: 0;
                    display: grid;
                    grid-template-columns: 1.35fr 1fr;
                    gap: clamp(0.75rem, 1.5vw, 1.25rem);
                    align-items: stretch;
                    overflow: hidden;
                }

                .cv-preview-col {
                    min-height: 0;
                    display: flex;
                    flex-direction: column;
                }

                /* ── Video preview column ─────────────────────────────── */

                .cv-preview-card {
                    background: var(--hx-card, #fff);
                    border: 1px solid var(--hx-line, #e2e8f0);
                    border-radius: var(--hx-radius, 16px);
                    box-shadow: var(--hx-shadow, 0 10px 40px rgba(15, 23, 42, 0.08));
                    overflow: hidden;
                    flex: 1;
                    min-height: 0;
                    display: flex;
                    flex-direction: column;
                }

                .cv-video-frame {
                    position: relative;
                    flex: 1;
                    min-height: 0;
                    display: flex;
                    flex-direction: column;
                }

                .cv-video-preview {
                    width: 100%;
                    flex: 1;
                    min-height: 140px;
                    height: auto;
                    padding-top: 0;
                    background: var(--hx-ink, #0f172a);
                    position: relative;
                    overflow: hidden;
                }

                .cv-video-preview > * {
                    position: absolute;
                    inset: 0;
                }

                .cv-video-preview video {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .cv-video-loading {
                    position: absolute;
                    inset: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    color: #94a3b8;
                    font-size: 0.85rem;
                }

                .cv-cam-off {
                    position: absolute;
                    inset: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    background: #111827;
                    color: #94a3b8;
                    font-size: 0.875rem;
                }

                .cv-cam-off-avatar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: var(--hx-blue, #2563eb);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.4rem;
                    font-weight: 700;
                }

                .cv-cam-status-badge {
                    position: absolute;
                    top: 0.7rem;
                    left: 0.7rem;
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    background: rgba(15, 23, 42, 0.55);
                    backdrop-filter: blur(6px);
                    -webkit-backdrop-filter: blur(6px);
                    color: #f8fafc;
                    font-size: 0.7rem;
                    font-weight: 600;
                    padding: 0.3rem 0.65rem;
                    border-radius: 999px;
                    z-index: 10;
                }

                .cv-cam-status-dot {
                    width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background: var(--hx-success, #16a34a);
                    flex-shrink: 0;
                }

                .cv-cam-status-badge.off .cv-cam-status-dot {
                    background: var(--hx-danger, #ef4444);
                }

                .cv-glass-bar {
                    position: absolute;
                    bottom: 0.75rem;
                    left: 50%;
                    transform: translateX(-50%);
                    display: flex;
                    gap: 0.5rem;
                    padding: 0.4rem;
                    background: rgba(255, 255, 255, 0.16);
                    border: 1px solid rgba(255, 255, 255, 0.28);
                    border-radius: 999px;
                    backdrop-filter: blur(14px);
                    -webkit-backdrop-filter: blur(14px);
                    z-index: 10;
                }

                .cv-glass-btn {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: rgba(15, 23, 42, 0.55);
                    border: 1px solid rgba(255, 255, 255, 0.18);
                    color: #f8fafc;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.95rem;
                    transition: background 0.15s, transform 0.1s;
                }

                .cv-glass-btn:hover {
                    background: rgba(15, 23, 42, 0.72);
                    transform: translateY(-1px);
                }

                .cv-glass-btn.off {
                    background: var(--hx-danger, #ef4444);
                    border-color: transparent;
                }

                .cv-preview-extra {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 0.75rem;
                    padding: 0.65rem 1rem;
                    border-top: 1px solid var(--hx-line, #e2e8f0);
                    flex-wrap: wrap;
                    flex-shrink: 0;
                }

                .cv-mirror-toggle {
                    display: flex;
                    align-items: center;
                    gap: 0.55rem;
                    font-size: 0.8rem;
                    font-weight: 550;
                    color: var(--hx-ink-2, #1e293b);
                    cursor: pointer;
                    user-select: none;
                }

                .cv-switch {
                    position: relative;
                    display: inline-flex;
                    width: 36px;
                    height: 20px;
                    flex-shrink: 0;
                }

                .cv-switch input {
                    position: absolute;
                    opacity: 0;
                    width: 100%;
                    height: 100%;
                    margin: 0;
                    cursor: pointer;
                    z-index: 1;
                }

                .cv-switch-track {
                    position: absolute;
                    inset: 0;
                    background: var(--hx-line, #e2e8f0);
                    border-radius: 999px;
                    transition: background 0.2s;
                }

                .cv-switch-track::after {
                    content: '';
                    position: absolute;
                    top: 2px;
                    left: 2px;
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    background: white;
                    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
                    transition: transform 0.2s;
                }

                .cv-switch input:checked ~ .cv-switch-track {
                    background: var(--hx-blue, #2563eb);
                }

                .cv-switch input:checked ~ .cv-switch-track::after {
                    transform: translateX(16px);
                }

                .cv-test-audio-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.45rem;
                    background: var(--hx-blue-soft, #eff6ff);
                    border: 1px solid var(--hx-blue-border, #bfdbfe);
                    color: var(--hx-blue, #2563eb);
                    font-size: 0.78rem;
                    font-weight: 600;
                    padding: 0.4rem 0.75rem;
                    border-radius: var(--hx-radius-sm, 10px);
                    cursor: pointer;
                    transition: background 0.15s, transform 0.1s;
                    font-family: inherit;
                    white-space: nowrap;
                }

                .cv-test-audio-btn:hover:not(:disabled) {
                    background: #dbeafe;
                }

                .cv-test-audio-btn.testing {
                    color: var(--hx-success, #16a34a);
                    background: rgba(22, 163, 74, 0.08);
                    border-color: rgba(22, 163, 74, 0.3);
                }

                .cv-test-audio-btn:disabled {
                    cursor: default;
                }

                /* ── Side column ──────────────────────────────────────── */

                .cv-side-col {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                    min-height: 0;
                    overflow: hidden;
                }

                .cv-card {
                    background: var(--hx-card, #fff);
                    border: 1px solid var(--hx-line, #e2e8f0);
                    border-radius: var(--hx-radius, 16px);
                    box-shadow: var(--hx-shadow-sm, 0 4px 16px rgba(15, 23, 42, 0.06));
                    padding: clamp(0.85rem, 1.5vh, 1.15rem);
                    transition: box-shadow 0.3s, border-color 0.3s;
                    min-height: 0;
                }

                .cv-system-card {
                    flex: 1;
                    overflow: hidden;
                }

                .cv-card.highlight {
                    border-color: var(--hx-blue-border, #bfdbfe);
                    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
                }

                .cv-card-title {
                    font-size: 0.7rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.07em;
                    color: var(--hx-muted, #64748b);
                    margin: 0 0 0.7rem;
                }

                .cv-device-row {
                    margin-bottom: 0.75rem;
                }

                .cv-device-row:last-child {
                    margin-bottom: 0;
                }

                .cv-mic-meter {
                    display: flex;
                    align-items: center;
                    gap: 0.55rem;
                    margin-top: 0.5rem;
                    padding: 0.4rem 0.65rem;
                    background: var(--hx-line-2, #f1f5f9);
                    border-radius: var(--hx-radius-sm, 10px);
                }

                .cv-mic-bars {
                    display: flex;
                    align-items: flex-end;
                    gap: 3px;
                    height: 14px;
                }

                .cv-mic-bars span {
                    width: 3px;
                    background: var(--hx-success, #16a34a);
                    border-radius: 2px;
                    animation: cvMicBar 1.1s ease-in-out infinite;
                }

                .cv-mic-bars span:nth-child(1) { height: 40%; animation-delay: 0s; }
                .cv-mic-bars span:nth-child(2) { height: 70%; animation-delay: 0.12s; }
                .cv-mic-bars span:nth-child(3) { height: 100%; animation-delay: 0.24s; }
                .cv-mic-bars span:nth-child(4) { height: 65%; animation-delay: 0.36s; }
                .cv-mic-bars span:nth-child(5) { height: 35%; animation-delay: 0.48s; }

                @keyframes cvMicBar {
                    0%, 100% { transform: scaleY(0.4); opacity: 0.7; }
                    50% { transform: scaleY(1); opacity: 1; }
                }

                .cv-mic-meter-label {
                    font-size: 0.72rem;
                    font-weight: 550;
                    color: var(--hx-success, #16a34a);
                }

                .cv-mic-meter-label.muted {
                    color: var(--hx-muted, #64748b);
                }

                .cv-mic-meter.muted {
                    background: var(--hx-line-2, #f1f5f9);
                }

                .cv-check-list {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 0.45rem;
                }

                /* ── Footer ───────────────────────────────────────────── */

                .cv-footer {
                    flex-shrink: 0;
                    padding: 0.75rem 1.5rem max(0.85rem, env(safe-area-inset-bottom, 0px));
                    padding-left: max(1.5rem, env(safe-area-inset-left, 0px));
                    padding-right: max(1.5rem, env(safe-area-inset-right, 0px));
                    border-top: 1px solid var(--hx-line, #e2e8f0);
                    background: rgba(255, 255, 255, 0.7);
                    backdrop-filter: blur(14px);
                    -webkit-backdrop-filter: blur(14px);
                }

                .cv-footer-actions {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    max-width: 1120px;
                    margin: 0 auto;
                }

                .cv-btn-primary,
                .cv-btn-secondary {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    font-size: 0.88rem;
                    font-weight: 650;
                    padding: 0.65rem 1.4rem;
                    border-radius: var(--hx-radius-sm, 10px);
                    cursor: pointer;
                    font-family: inherit;
                    letter-spacing: 0.01em;
                    transition: all 0.15s;
                    min-height: 42px;
                }

                .cv-btn-primary {
                    background: var(--hx-blue, #2563eb);
                    border: none;
                    color: white;
                }

                .cv-btn-primary:hover:not(:disabled) {
                    background: var(--hx-blue-hover, #1d4ed8);
                    transform: translateY(-1px);
                    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.3);
                }

                .cv-btn-primary:disabled {
                    opacity: 0.55;
                    cursor: not-allowed;
                }

                .cv-btn-secondary {
                    background: var(--hx-card, #fff);
                    border: 1.5px solid var(--hx-line, #e2e8f0);
                    color: var(--hx-ink-2, #1e293b);
                }

                .cv-btn-secondary:hover {
                    background: var(--hx-line-2, #f1f5f9);
                    border-color: #cbd5e1;
                }

                .cv-encryption-note {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.4rem;
                    text-align: center;
                    font-size: 0.7rem;
                    color: var(--hx-muted, #64748b);
                    margin: 0.55rem 0 0;
                }

                /* ── Responsive ───────────────────────────────────────── */

                @media (max-width: 960px) {
                    .cv-grid {
                        grid-template-columns: 1fr 0.95fr;
                    }
                }

                @media (max-width: 860px), (max-height: 720px) and (max-width: 1100px) {
                    .cv-grid {
                        grid-template-columns: 1fr;
                        grid-template-rows: minmax(0, 1fr) auto;
                    }

                    .cv-side-col {
                        flex-direction: row;
                        flex-wrap: wrap;
                        gap: 0.65rem;
                        overflow: hidden;
                    }

                    .cv-side-col > .cv-card {
                        flex: 1 1 220px;
                    }

                    .cv-system-card {
                        flex: 1 1 220px;
                    }

                    .cv-check-list {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 0.35rem 0.75rem;
                    }
                }

                @media (max-width: 768px) {
                    .cv-header {
                        padding: 0.6rem 0.85rem;
                        padding-left: max(0.85rem, env(safe-area-inset-left, 0px));
                        padding-right: max(0.85rem, env(safe-area-inset-right, 0px));
                        gap: 0.5rem;
                    }

                    .cv-back-link span { display: none; }
                    .cv-brand { margin: 0 auto; }
                    .cv-brand-name { display: none; }
                    .cv-hipaa-badge span { display: none; }
                    .cv-hipaa-badge { padding: 0.35rem; }

                    .cv-main {
                        padding: 0.55rem 0.75rem;
                    }

                    .cv-title-wrap {
                        margin-bottom: 0.45rem;
                    }

                    .cv-subtitle {
                        display: none;
                    }

                    .cv-preview-extra {
                        justify-content: space-between;
                        padding: 0.5rem 0.75rem;
                    }

                    .cv-footer {
                        padding: 0.6rem 0.85rem max(0.7rem, env(safe-area-inset-bottom, 0px));
                        padding-left: max(0.85rem, env(safe-area-inset-left, 0px));
                        padding-right: max(0.85rem, env(safe-area-inset-right, 0px));
                    }

                    .cv-footer-actions {
                        flex-direction: row;
                        gap: 0.5rem;
                    }

                    .cv-btn-primary,
                    .cv-btn-secondary {
                        flex: 1;
                        min-height: 40px;
                        padding: 0.55rem 0.75rem;
                        font-size: 0.82rem;
                    }

                    .cv-encryption-note {
                        display: none;
                    }

                    .cv-glass-btn {
                        width: 36px;
                        height: 36px;
                        font-size: 0.85rem;
                    }
                }

                @media (max-height: 640px) {
                    .cv-title-wrap { display: none; }
                    .cv-encryption-note { display: none; }
                    .cv-system-card { display: none; }
                }
            `}</style>
        </div>
    );
}

function SystemCheckRow({ label, ready }: { label: string; ready: boolean }) {
    return (
        <li className={`cv-check-row ${ready ? 'ok' : 'pending'}`}>
            <span className="cv-check-icon">
                <FontAwesomeIcon icon={ready ? faCheckCircle : faSpinner} spin={!ready} />
            </span>
            <span className="cv-check-label">{label}</span>
            <span className="cv-check-status">{ready ? 'Ready' : 'Checking…'}</span>

            <style>{`
                .cv-check-row {
                    display: flex;
                    align-items: center;
                    gap: 0.65rem;
                    font-size: 0.85rem;
                    color: var(--hx-ink-2, #1e293b);
                }

                .cv-check-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 20px;
                    font-size: 0.95rem;
                    color: var(--hx-muted-2, #94a3b8);
                    flex-shrink: 0;
                }

                .cv-check-row.ok .cv-check-icon {
                    color: var(--hx-success, #16a34a);
                }

                .cv-check-label {
                    flex: 1;
                    font-weight: 550;
                }

                .cv-check-status {
                    font-size: 0.75rem;
                    color: var(--hx-muted, #64748b);
                    font-weight: 550;
                }

                .cv-check-row.ok .cv-check-status {
                    color: var(--hx-success, #16a34a);
                }
            `}</style>
        </li>
    );
}
